import interpolateComponents from '@automattic/interpolate-components';
import { ElementRef } from 'react';

import { validateApplication } from 'api/ats';
import { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box';
import { CheckboxField } from 'components/Form/CheckboxField';
import { FileUploadField } from 'components/Form/FileUploadField';
import { Form, FormUsageContext } from 'components/Form/Form';
import { GeneralError } from 'components/Form/GeneralError';
import { HiddenField } from 'components/Form/HiddenField';
import { InputField } from 'components/Form/InputField';
import { MultiInputField } from 'components/Form/MultiInputField';
import { RequestedAttachmentUserField } from 'components/Form/RequestedAttachmentUserField';
import { RequestedTextUserField } from 'components/Form/RequestedTextUserField';
import { RequestedURLUserField } from 'components/Form/RequestedURLUserField';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { InfoBox } from 'components/InfoBox';
import { LegacyTooltip } from 'components/LegacyTooltip';
import { Link } from 'components/Link';
import { BodySmall } from 'components/Text/BodySmall';
import { useUserEnvironment } from 'store/hooks/useUserEnvironment';
import { gutterWidth } from 'theme/theme';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNewResume = (user: ApiUser | null | undefined, getValue: any) =>
  user?.resumeId &&
  getValue('resumeS3FileId') &&
  getValue('resumeS3FileId') !== user.resumeId;

function AtsConsentField({
  isEU,
  disabled,
}: {
  isEU: boolean;
  disabled?: boolean;
}) {
  return isEU ? (
    <CheckboxField
      name="consentAts"
      disabled={disabled}
      label={
        <BodySmall lineHeight={1.375} pt="calc(24px - 1em)">
          {interpolateComponents({
            mixedString: getText(
              'By using the Idealist Application system, I consent to ' +
                'sharing my resume and other personal data with potential ' +
                'employers in accordance with Idealist’s ' +
                '{{linkPrivacyPolicy}}Privacy Policy{{/linkPrivacyPolicy}} ' +
                'and {{linkTermsOfService}}Terms of Service{{/linkTermsOfService}}. ' +
                'If you request us to share your Application Information ' +
                'with an Organization outside Europe, you explicitly ' +
                'consent to the transfer of your Application ' +
                'Information outside Europe.',
            ),
            components: {
              linkPrivacyPolicy: (
                <Link target="_blank" href={getText('/en/privacy-policy')} />
              ),
              linkTermsOfService: (
                <Link target="_blank" href={getText('/en/terms-of-service')} />
              ),
            },
          })}
        </BodySmall>
      }
    />
  ) : (
    <InfoBox $type="info" data-qa-id="consent-ats-text">
      <BodySmall lineHeight={1.375}>
        {interpolateComponents({
          mixedString: getText(
            'I acknowledge that use of the Idealist Applicant Tracking System is ' +
              "subject to Idealist's " +
              '{{linkPrivacyPolicy}}Privacy Policy{{/linkPrivacyPolicy}} ' +
              'and {{linkTermsOfService}}Terms of Service{{/linkTermsOfService}}.',
          ),
          components: {
            linkPrivacyPolicy: (
              <Link target="_blank" href={getText('/en/privacy-policy')} />
            ),
            linkTermsOfService: (
              <Link target="_blank" href={getText('/en/terms-of-service')} />
            ),
          },
        })}
      </BodySmall>
    </InfoBox>
  );
}

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (...args: Array<any>) => any;
  formRef: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    current: null | ElementRef<any>;
  };
  listingId: string;
  listingType: ApiListingApplicationListingType;
  requestedAttachments: ApiRequestedAttachment[];
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValues: Record<string, any>;
  isPreview?: boolean;
};

export function ListingViewApplicationFormContainer({
  analyticsTitle,
  usageContext,
  onSubmit,
  formRef,
  isPreview,
  listingId,
  listingType,
  requestedAttachments,
  initialValues,
}: Props) {
  const { user, environment } = useUserEnvironment();
  const initialUser = user
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }
    : {};
  const allFieldsRequired =
    requestedAttachments.filter((ra) => !ra.isRequired).length === 0;
  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      data-qa-id="listing-application-form"
      headerOffset={132}
      initialValues={{
        ...initialUser,
        id: listingId,
        resumeS3FileId:
          listingType !== 'VOLOP' ? user?.resumeId || '' : undefined,
        consentAts: environment?.isEU ? false : undefined,
        requestedAttachments: Array(requestedAttachments.length).fill(null),
        type: listingType,
        replaceCurrentResume: false,
        ...initialValues,
      }}
      formRef={formRef}
      onSubmit={onSubmit}
      render={({
        clearErrors,
        handleError,
        submitting,
        updateValueWith,
        getValue,
      }) => (
        <>
          {allFieldsRequired ? (
            <Box mt={-12} mb={3}>
              <InfoBox $type="warning" $small>
                <BodySmall>{getText('All fields are required')}</BodySmall>
              </InfoBox>
            </Box>
          ) : null}

          <GeneralError />

          <HiddenField name="requestedAttachmentsHash" />

          <Box mt={24}>
            <InputField
              disabled={isPreview}
              name="firstName"
              label={
                !allFieldsRequired
                  ? `${getText('First Name')}*`
                  : `${getText('First Name')}`
              }
              placeholder={getText('Casey')}
            />

            <InputField
              disabled={isPreview}
              name="lastName"
              label={
                !allFieldsRequired
                  ? `${getText('Last Name')}*`
                  : `${getText('Last Name')}`
              }
              placeholder={getText('Smith')}
            />

            <InputField
              disabled={isPreview}
              name="email"
              label={
                !allFieldsRequired
                  ? `${getText('Email')}*`
                  : `${getText('Email')}`
              }
              type="email"
              placeholder={getText('casey.smith@example.com')}
            />

            {/*
         NOTE: the requestedAttachments field is used three times here:
         once for type="TEXT", once for type="URL", and once for type="FILE". In each
         instance, the other types will be null.
        */}

            {requestedAttachments.length > 0 && (
              <MultiInputField
                hideControls
                component={RequestedTextUserField}
                maxInputs={requestedAttachments.length}
                name="requestedAttachments"
                extraData={requestedAttachments.map((ra) => ({
                  ...ra,
                  label:
                    !allFieldsRequired && ra.isRequired
                      ? `${ra.label}*`
                      : ra.label,
                  downloadAttachment: 'false',
                  fileType: 'LISTING_APPLICATION',
                  disabled: isPreview,
                }))}
                // @ts-expect-error TS(2322): Type '{ hideControls: true; component: ({ download... Remove this comment to see the full error message
                listingType={listingType}
              />
            )}

            {requestedAttachments.length > 0 && (
              <MultiInputField
                hideControls
                component={RequestedURLUserField}
                maxInputs={requestedAttachments.length}
                name="requestedAttachments"
                onError={(error) => {
                  handleError(error);
                }}
                extraData={requestedAttachments.map((ra) => ({
                  ...ra,
                  label:
                    !allFieldsRequired && ra.isRequired
                      ? `${ra.label}*`
                      : ra.label,
                  downloadAttachment: 'false',
                  fileType: 'LISTING_APPLICATION',
                  disabled: isPreview,
                }))}
                placeholder={getText('https://example.org')}
                // @ts-expect-error TS(2322): Type '{ hideControls: true; component: ({ download... Remove this comment to see the full error message
                listingType={listingType}
              />
            )}

            {listingType !== 'VOLOP' ? (
              <FileUploadField
                disabled={isPreview}
                label={
                  !allFieldsRequired
                    ? `${getText('Resume')}*`
                    : `${getText('Resume')}`
                }
                description={getText('Resume must be uploaded in PDF format')}
                downloadAttachment={false}
                name="resumeS3FileId"
                fileType="USER_RESUME"
                accept=".pdf"
                onError={(error) => {
                  handleError(error);
                }}
                showDate={!isNewResume(user, getValue)}
                onUpdateValue={async (e) => {
                  try {
                    await validateApplication(
                      { resumeS3FileId: e },
                      { partial: true },
                    );
                    updateValueWith('resumeS3FileId', () => e);
                    clearErrors();
                  } catch (error) {
                    updateValueWith(
                      'resumeS3FileId',
                      () => e,
                      () => {
                        handleError(error as Error);
                      },
                    );
                  }
                }}
                extraAnalyticsData={{
                  context: 'Listing Application Form',
                  listing_type: listingType,
                  file_label: 'Resume',
                }}
              />
            ) : null}

            {user && isNewResume(user, getValue) && (
              <Box mt={-26}>
                <CheckboxField
                  disabled={isPreview}
                  name="replaceCurrentResume"
                  label={
                    <Box display="flex" alignItems="center">
                      {getText(
                        'Replace my current resume on file with this one',
                      )}
                      <Box position="relative" zIndex={1} mt="1px" ml="6px">
                        <LegacyTooltip>
                          {getText(`You already have a resume saved with your account.
                              If you check this box, we'll save this new resume
                              to your account.`)}
                        </LegacyTooltip>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            )}

            {requestedAttachments.length > 0 && (
              <MultiInputField
                hideControls
                component={RequestedAttachmentUserField}
                maxInputs={requestedAttachments.length}
                name="requestedAttachments"
                onError={(error) => {
                  handleError(error);
                }}
                extraData={requestedAttachments.map((ra) => ({
                  ...ra,
                  analyticsLabel: ra.label,
                  label:
                    !allFieldsRequired && ra.isRequired
                      ? `${ra.label}*`
                      : ra.label,
                  downloadAttachment: 'false',
                  fileType: 'LISTING_APPLICATION',
                  disabled: isPreview,
                  listingType,
                }))}
              />
            )}

            <AtsConsentField
              isEU={Boolean(environment?.isEU)}
              disabled={isPreview}
            />

            <Box mt={gutterWidth} display="flex" justifyContent="flex-end">
              <SubmitButton size="large" disabled={isPreview || submitting}>
                {listingType === 'EVENT'
                  ? getText('Join')
                  : getText('Submit Application')}
              </SubmitButton>
            </Box>
          </Box>
        </>
      )}
    />
  );
}
